<script setup lang="ts">
import { watch, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useContactDetails } from '../../composables/useContactDetails'
import PhoneInputControl from '../PhoneInputControl.vue'
import type { IPhoneOutput } from '../../types/CommonTypes'

const { t } = useI18n()

const {
  // State
  contactForm,
  formErrors,
  isFormValid,
  isLoading,
  predefinedQuestions,
  securityFormData,
  showPasswordPattern,
  hasAtLeastOneAnswer,
  primaryPhoneModel,
  mobilePhoneModel,

  // Methods
  validateCurrentPassword,
  validateNewPassword,
  validateConfirmPassword,
  validateFirstName,
  validateLastName,
  validateEmailAddress,
  loadProfileData,
  handlePasswordChange,
  handleAccountDetailsChange,
  handleSecurityQuestionsChange,
  loadContactDetailsData
} = useContactDetails()

// Phone input refs for validation
const primaryPhoneControl = ref<InstanceType<typeof PhoneInputControl> | null>(null)
const mobilePhoneControl = ref<InstanceType<typeof PhoneInputControl> | null>(null)

// Watch for changes in password fields to update form validity
watch(
  () => contactForm.value.currentPassword,
  () => {
    if (contactForm.value.currentPassword) {
      validateCurrentPassword()
    } else {
      formErrors.value.currentPassword = ''
    }
  }
)

watch(
  () => contactForm.value.newPassword,
  async () => {
    if (contactForm.value.newPassword) {
      await validateNewPassword()
    } else {
      formErrors.value.newPassword = ''
    }
    // Also validate confirm password if it has a value, since it depends on new password
    if (contactForm.value.confirmPassword) {
      validateConfirmPassword()
    }
  }
)

watch(
  () => contactForm.value.confirmPassword,
  () => {
    if (contactForm.value.confirmPassword) {
      validateConfirmPassword()
    } else {
      formErrors.value.confirmPassword = ''
      formErrors.value.passwordMatch = ''
    }
  }
)

onMounted(async () => {
  await loadProfileData()
  await loadContactDetailsData()
})
</script>

<template>
  <div class="contact-details-screen">
    <!-- Password Change Section -->
    <div class="form-section">
      <h3 class="section-title">{{ t('accountInfo.contactDetails.passwordChangeTitle') }}</h3>
      
      <div class="form-group">
        <label class="form-label">{{ t('accountInfo.contactDetails.currentPassword') }}</label>
        <input 
          v-model="contactForm.currentPassword"
          type="password"
          class="form-input"
          :class="{ error: formErrors.currentPassword }"
          @blur="validateCurrentPassword"
        />
        <div v-if="formErrors.currentPassword" class="error-message">{{ formErrors.currentPassword }}</div>
      </div>

      <div class="form-group">
        <label class="form-label">{{ t('accountInfo.contactDetails.newPassword') }}</label>
        <input 
          v-model="contactForm.newPassword"
          type="password"
          class="form-input"
          :class="{ error: formErrors.newPassword }"
          @blur="validateNewPassword"
        />
        <div v-if="formErrors.newPassword" class="error-message">{{ formErrors.newPassword }}</div>
        <div v-if="showPasswordPattern" class="row regRow password-pattern-div bg-light">
              <div
                class="col-12"
                v-html="t('validators.passwordPattern')">
              </div>
        </div>
      </div>

      <div class="form-group">
        <label class="form-label">{{ t('accountInfo.contactDetails.confirmPassword') }}</label>
        <input 
          v-model="contactForm.confirmPassword"
          type="password"
          class="form-input"
          :class="{ error: formErrors.confirmPassword || formErrors.passwordMatch }"
          @blur="validateConfirmPassword"
        />
        <div v-if="formErrors.confirmPassword" class="error-message">{{ formErrors.confirmPassword }}</div>
        <div v-if="formErrors.passwordMatch" class="error-message">{{ formErrors.passwordMatch }}</div>
      </div>

      <button 
        @click="handlePasswordChange"
        :disabled="!isFormValid || isLoading"
        class="form-button primary"
      >
        {{ t('accountInfo.contactDetails.changePassword') }}
      </button>
    </div>

    <!-- Security Questions Section -->
    <div class="form-section">
      <h3 class="section-title">{{ t('accountInfo.contactDetails.securityQuestionsTitle') }}</h3>
      <p class="section-description">{{ t('consultantCorner.securityQuestions.subHeader') }}</p>

      <!-- Question 1: Predefined dropdown question -->
      <div class="question-group">
        <h5>{{ t('consultantCorner.securityQuestions.question1Label') }}</h5>
        <div class="form-group">
          <label class="form-label">{{ t('consultantCorner.securityQuestions.selectQuestion') }}</label>
          <select
            v-model="securityFormData.selectedQuestionNumber"
            class="form-input"
          >
            <option :value="null">{{ t('consultantCorner.securityQuestions.selectQuestionPlaceholder') }}</option>
            <option v-for="q in predefinedQuestions" :key="q.value" :value="q.value">
              {{ q.text }}
            </option>
          </select>
        </div>
        <div class="form-group">
          <label class="form-label">{{ t('consultantCorner.securityQuestions.answerLabel') }}</label>
          <input
            v-model="securityFormData.genericQuestionResponse"
            type="text"
            class="form-input"
            :placeholder="t('consultantCorner.securityQuestions.answerPlaceholder')"
          />
        </div>
      </div>

      <!-- Question 2: Custom question -->
      <div class="question-group">
        <h5>{{ t('consultantCorner.securityQuestions.question2Label') }}</h5>
        <div class="form-group">
          <label class="form-label">{{ t('consultantCorner.securityQuestions.customQuestionLabel') }}</label>
          <input
            v-model="securityFormData.customQuestion"
            type="text"
            class="form-input"
            :placeholder="t('consultantCorner.securityQuestions.customQuestionPlaceholder')"
          />
        </div>
        <div class="form-group">
          <label class="form-label">{{ t('consultantCorner.securityQuestions.answerLabel') }}</label>
          <input
            v-model="securityFormData.customQuestionResponse"
            type="text"
            class="form-input"
            :placeholder="t('consultantCorner.securityQuestions.answerPlaceholder')"
          />
        </div>
      </div>

      <button
        @click="handleSecurityQuestionsChange"
        :disabled="!hasAtLeastOneAnswer"
        class="form-button primary"
      >
        {{ t('accountInfo.contactDetails.saveSecurityQuestions') }}
      </button>
    </div>

    <!-- Account Details Section -->
    <div class="form-section">
      <h3 class="section-title">{{ t('accountInfo.contactDetails.accountDetailsTitle') }}</h3>
      
      <div class="form-row">
        <div class="form-group">
          <label class="form-label">{{ t('accountInfo.contactDetails.firstName') }}</label>
          <input 
            v-model="contactForm.firstName"
            type="text"
            class="form-input"
            :class="{ error: formErrors.firstName }"
            @input="validateFirstName"
            @blur="validateFirstName"
          />
          <div v-if="formErrors.firstName" class="error-message">{{ formErrors.firstName }}</div>
        </div>
        
        <div class="form-group">
          <label class="form-label">{{ t('accountInfo.contactDetails.lastName') }}</label>
          <input 
            v-model="contactForm.lastName"
            type="text"
            class="form-input"
            :class="{ error: formErrors.lastName }"
            @input="validateLastName"
            @blur="validateLastName"
          />
          <div v-if="formErrors.lastName" class="error-message">{{ formErrors.lastName }}</div>
        </div>
      </div>

      <div class="form-group">
        <label class="form-label">{{ t('accountInfo.contactDetails.emailAddress') }}</label>
        <input 
          v-model="contactForm.emailAddress"
          type="email"
          class="form-input"
          :class="{ error: formErrors.emailAddress }"
          @input="validateEmailAddress"
          @blur="validateEmailAddress"
        />
        <div v-if="formErrors.emailAddress" class="error-message">{{ formErrors.emailAddress }}</div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label class="form-label">{{ t('accountInfo.contactDetails.phoneNumber') }}</label>
          <PhoneInputControl
            ref="primaryPhoneControl"
            v-model="primaryPhoneModel"
            :required="false"
            :showExtension="false"
            :showPhoneType="false"
            :placeholder="t('accountInfo.contactDetails.phonePlaceholder')"
          />
        </div>
        
        <div class="form-group">
          <label class="form-label">{{ t('accountInfo.contactDetails.mobileNumber') }}</label>
          <PhoneInputControl
            ref="mobilePhoneControl"
            v-model="mobilePhoneModel"
            :required="false"
            :showExtension="false"
            :showPhoneType="false"
            :placeholder="t('accountInfo.contactDetails.mobilePlaceholder')"
          />
        </div>
      </div>

      <button 
        @click="handleAccountDetailsChange"
        :disabled="isLoading"
        class="btn primaryBtn"
      >
        {{ t('accountInfo.contactDetails.saveAccountDetails') }}
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.contact-details-screen {
  display: flex;
  flex-direction: column;
  gap: $spacing-8;
}

.form-section {
  background: var(--vt-c-white-soft);
  border-radius: $radius-md;
  padding: $spacing-6;
  border: 1px solid #e9ecef;
  margin: 0 0 !important;
}

.section-title {
  @include typography-h4;
  @include font-weight-semibold;
  color: #333;
  margin: 0 0 16px 0;
}

.section-description {
  @include typography-small;
  color: #666;
  margin: 0 0 20px 0;
  line-height: 1.4;
}

.form-group {
  margin-bottom: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: $gap-md;
}

.form-label {
  @include typography-small;
  @include font-weight-medium;
  display: block;
  color: #333;
  margin-bottom: 6px;
  width: 100% !important;
}

.form-input {
  width: 100%;
  padding: $spacing-2-5 $spacing-3;
  border: 1px solid #e0e0e0;
  @include typography-small;
  border-radius: $radius-sm;
  transition: border-color 0.2s ease;
}

.form-input:focus {
  outline: none;
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  box-shadow: 0 0 0 2px rgba(var(--vt-c-teal-dark), 0.1);
}

.form-input.error {
  border-color: #dc3545;
}

.form-button {
  padding: $spacing-3 $spacing-6;
  border: none;
  @include typography-small;
  @include font-weight-medium;
  border-radius: $radius-md;
  cursor: pointer;
  transition: all 0.2s ease;
}

.form-button.primary {
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: white;
}

.form-button.primary:hover:not(:disabled) {
  background-color: var(--buttonPrimaryHoverColor, #145258);
}

.form-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.error-message {
  @include typography-caption;
  color: #dc3545;
  margin-top: 4px;
}

.password-pattern-info {
  @include typography-caption;
  color: #6c757d;
  margin-top: 4px;
  line-height: 1.4;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }

  .form-section {
    padding: $spacing-4;
  }
}

/* Security questions styling */
.question-group {
  margin-bottom: $spacing-5;
  padding: $spacing-4;
  background-color: var(--vt-c-white-soft);
  border-radius: $radius-md;
  border: 1px solid #e9ecef;
}

.question-group h5 {
  @include typography-body-large;
  @include font-weight-semibold;
  color: #333;
  margin-bottom: $spacing-4;
  border-bottom: 2px solid var(--color-teal, var(--vt-c-teal-dark));
  padding-bottom: $spacing-2;
}

/* Override PhoneInputControl to match existing form styling */
.phone-input-control {
  .phone-input-group {
    .country-select {
      border: 1px solid #e0e0e0;
      border-radius: $radius-sm 0 0 $radius-sm;
      @include typography-small;
    }
    .phone-input {
      border: 1px solid #e0e0e0;
      border-radius: 0 $radius-sm $radius-sm 0;
      @include typography-small;
    }
  }
  .phone-input.error {
    border-color: #dc3545;
  }
  .country-select.error {
    border-color: #dc3545;
  }
  .phone-input:focus {
    outline: none;
    border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
    box-shadow: 0 0 0 2px rgba(var(--vt-c-teal-dark), 0.1);
  }
  .country-select:focus {
    outline: none;
    border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
    box-shadow: 0 0 0 2px rgba(var(--vt-c-teal-dark), 0.1);
  }
}
</style>
